import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ujmsMotionsQueue, ujmsRuleMotion } from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, fmtDT, cls, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { RuleCiteButton, appendCitation } from "../RuleCite";
import { GavelIcon, Scale } from "lucide-react";

const RULINGS = ["GRANTED", "DENIED", "GRANTED_IN_PART", "DENIED_WITHOUT_PREJUDICE", "RESERVED"];

export default function MotionsQueue() {
  const [scope, setScope] = useState("mine");
  const [d, setD] = useState(null);
  const [ruling, setRuling] = useState(null);
  const load = useCallback(() => {
    ujmsMotionsQueue(scope).then((r) => setD(r.data)).catch(() => {});
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="motions-queue">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Motions Queue</h1>
        <p className="text-sm text-slate-500">Pending motions awaiting judicial ruling. Every ruling is entered on the official docket.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
        <div className="xl:col-span-2">
          <Panel title="Pending motions" testId="mq-pending"
            action={
              <div className="flex gap-1.5">
                {["mine", "all"].map((s) => (
                  <button key={s} onClick={() => setScope(s)} data-testid={`mq-scope-${s}`}
                    className={cls("text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded-sm border",
                      scope === s ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100")}>
                    {s === "mine" ? "My Cases" : "All"}
                  </button>
                ))}
              </div>}>
            {!d ? (
              <div className="text-sm text-slate-400 p-3">Loading…</div>
            ) : (d.pending || []).length === 0 ? (
              <EmptyState icon={Scale} title="No motions awaiting ruling" note="Motions filed by the parties appear here for disposition." testId="mq-empty" />
            ) : (
              <table className="w-full border-collapse">
                <thead><tr><th className={thCls}>Filed</th><th className={thCls}>Case</th><th className={thCls}>Motion</th><th className={thCls}>Movant</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
                <tbody>
                  {(d.pending || []).map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50" data-testid={`mq-row-${m.id}`}>
                      <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(m.received_at)}</td>
                      <td className={`${tdCls} font-mono text-xs`}>
                        <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${m.case_id}`}>{m.case_number}</Link>
                        <div className="text-slate-400 truncate max-w-[160px]">{m.caption}</div>
                      </td>
                      <td className={tdCls}>{m.filing_title || m.title}</td>
                      <td className={`${tdCls} text-xs`}>{m.filed_by}</td>
                      <td className={tdCls}><Badge value={m.status} /></td>
                      <td className={tdCls}>
                        <button className={btnPrimary} onClick={() => setRuling(m)} data-testid={`mq-rule-${m.id}`}>Rule</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </div>
        <Panel title="Recent rulings" testId="mq-recent">
          {(d?.recent_rulings || []).length === 0 ? (
            <div className="text-sm text-slate-400 p-3">No rulings yet.</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {(d?.recent_rulings || []).map((r) => (
                <div key={r.id} className="px-3 py-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-blue-900">{r.case_number}</span>
                    <Badge value={r.ruling} />
                  </div>
                  <div className="text-slate-700 text-xs mt-0.5">{r.motion_title}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{fmtDT(r.created_at)} · {r.ruled_by}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <RulingDialog m={ruling} onClose={() => setRuling(null)} onDone={load} />
    </div>
  );
}

function RulingDialog({ m, onClose, onDone }) {
  const [f, setF] = useState({ ruling: "", ruling_text: "" });
  useEffect(() => { setF({ ruling: "", ruling_text: "" }); }, [m?.id]);
  const submit = async () => {
    try {
      await ujmsRuleMotion(m.id, f);
      toast.success(`MOTION_RULING docketed — ${f.ruling.replace(/_/g, " ")}`);
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Ruling failed"); }
  };
  return (
    <Dialog open={!!m} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mq-ruling-dialog">
        <DialogHeader><DialogTitle>Rule on motion</DialogTitle></DialogHeader>
        <div className="text-sm bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
          <div className="font-mono text-xs text-blue-900">{m?.case_number}</div>
          <div className="text-slate-800">{m?.filing_title || m?.title}</div>
          <div className="text-xs text-slate-400">Filed by {m?.filed_by}</div>
        </div>
        <Field label="Ruling" required>
          <select className={selectCls} value={f.ruling} onChange={(e) => setF({ ...f, ruling: e.target.value })} data-testid="mq-ruling-select">
            <option value="">Select ruling…</option>
            {RULINGS.map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        <Field label="Ruling text (docketed)">
          <div className="space-y-1.5">
            <textarea className={`${inputCls} h-20`} value={f.ruling_text}
              onChange={(e) => setF({ ...f, ruling_text: e.target.value })}
              placeholder="For the reasons stated on the record…" data-testid="mq-ruling-text" />
            <RuleCiteButton testId="mq-cite-rule" onCite={(c) => setF((p) => ({ ...p, ruling_text: appendCitation(p.ruling_text, c) }))} />
          </div>
        </Field>
        {f.ruling === "RESERVED" && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-1.5">
            Reserving keeps the motion pending (UNDER_REVIEW); the reservation is still docketed.
          </div>
        )}
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={!f.ruling} data-testid="mq-ruling-submit">Enter Ruling</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
